import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { selectTranscript } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import {
  getClipBroadcastId,
  getIsDuplicateRecording,
  isFullLengthClip,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { AddMenuItem } from '../TimelinePlayerActionBar/AddMenu/types';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../timeUtils';
import { endChapterCreation, startChapterCreation, timelineActionsStore } from '../store/timelineActions';
import { canCreateANewChapter } from '../utils';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { showErrorToast } from '@/libs/toast/toast';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const useAddChapterItem = (): AddMenuItem | null => {
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { clipData } = useClipsContext();

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  // Chapters belong to the full-length recording, so they can only be created there.
  const isChapterCreationAllowed = useMemo(
    () => !!clipData?.asset_metadata && isFullLengthClip(clipData) && !getIsDuplicateRecording(clipData),
    [clipData]
  );

  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const clipBroadcastId = useMemo(() => getClipBroadcastId(clipData), [clipData]);
  const transcript = useContentMetadataStore(selectTranscript(clipBroadcastId ?? ''));
  const { createNewChapter } = useTranscriptPage();

  const { isCreatingChapter } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);

  const isTranscriptEmpty = !transcript?.length;
  const lastTranscriptWordEndTime = useMemo(
    () =>
      transcript?.reduce((maxWordEndTime, word) => {
        const wordEndTime = typeof word.end_time === 'number' ? word.end_time : word.start_time;
        return wordEndTime > maxWordEndTime ? wordEndTime : maxWordEndTime;
      }, Number.NEGATIVE_INFINITY) ?? Number.NEGATIVE_INFINITY,
    [transcript]
  );

  const currentSegment = useMemo(() => getSegmentAtCurrentFrame(currentTime, clipData), [currentTime, clipData]);
  const chapterTime = useMemo(() => {
    if (!currentSegment) {
      return null;
    }

    return isSplitsEnabled ? currentTime : getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);
  }, [clipData, currentSegment, currentTime, isSplitsEnabled]);

  const isAfterLastTranscriptWordWithoutSplits =
    !isSplitsEnabled &&
    !isTranscriptEmpty &&
    typeof chapterTime === 'number' &&
    chapterTime > lastTranscriptWordEndTime;

  const isDisabled =
    !chapters ||
    isTranscriptEmpty ||
    isAfterLastTranscriptWordWithoutSplits ||
    isCreatingChapter ||
    !canCreateANewChapter({
      currentTime,
      chapters,
      clipData
    });

  const reasonForDisabled = isTranscriptEmpty
    ? 'Transcript is empty'
    : isAfterLastTranscriptWordWithoutSplits
      ? 'Cannot create chapter after transcript ends'
      : undefined;

  const onAddChapter = useCallback(async (): Promise<void> => {
    if (isCreatingChapter || !isChapterCreationAllowed) {
      return;
    }
    startChapterCreation();
    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);

    if (!currentSegment) {
      showErrorToast('No segment found at the current time. Please try again.');
      endChapterCreation();
      return;
    }
    const chapterTime = isSplitsEnabled
      ? currentTime
      : getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);

    createNewChapter('New Chapter', chapterTime);

    endChapterCreation();
  }, [clipData, createNewChapter, currentTime, isChapterCreationAllowed, isCreatingChapter, isSplitsEnabled]);

  return useMemo(() => {
    if (!chapters || !isChapterCreationAllowed) {
      return null;
    }

    return {
      id: 'add-chapter',
      label: isCreatingChapter ? 'Adding Chapter...' : 'Add Chapter',
      iconName: 'IconList',
      onClick: onAddChapter,
      isDisabled,
      reasonForDisabled
    };
  }, [chapters, isChapterCreationAllowed, isCreatingChapter, onAddChapter, isDisabled, reasonForDisabled]);
};

export default useAddChapterItem;
